import {
  IMoveOrResizeManyPayload,
  IUpdateMfilesPropertiesOfMany,
  IUpdateOwnPropertiesOfMany,
  IDragNDropPayload,
  IRemoveViewItemPayload,
} from "../interfaces/payloads/itemReducers";
import { MFILES_DATE_VALUE_FORMAT } from "../../backend/constants/common";
import { createModel } from "@rematch/core";
import { addMinutes, differenceInMinutes, parseISO } from "date-fns";
import { formatWithLocale as format } from "../../gantt/helpers/DateHelpers";
import { IItem, IItemProperty, IItemText, IItemTexts } from "../../backend/interfaces/IItem";
import IItemsState from "../interfaces/states/IItemsState";
import {
  IConfiguration,
  IDragAndDropConfigurationItem,
  ILinkConfigurationItem,
  IObjectTypeConfigurationItem,
} from "../../backend/interfaces/configuration/IConfiguration";
import { 
  fetchChangedItemsAsync, 
  fetchCheckedoutItems, 
  fetchSpecificItemPropertiesAsync, 
  getItemsWithPropertiesAsync, 
  getSearchItemsAsync, 
  getSearchItemsWithPropertiesAsync,
  getSelectedItemsAsync, 
  getViewItemsAsync,
  getViewItemsWithPropertiesAsync, 
  getItemsAsync,
  getPropertiesOfMultipleObjectsAsync,
  mapMFilesItemWithPropertiesToItem
} from "../../backend/services/FetchItemsService";
import { produce } from "immer";
import { getTestData } from "../../backend/devmode/TestData";
import { IMfilesPropertyUpdate, IObjectUpdate } from "../interfaces/IObjectUpdate";
import {
  getProperty,
  toggleInLookupPropertyValue,
  removeFromLookupPropertyValue,
  isInLookupPropertyValue,
} from "../../backend/helpers/itemHelpers";
import { updateManyObjectsAsync } from "../../backend/services/UpdateItemsService";
import { revertUpdate } from "../../backend/helpers/undoHelpers";
import { PropertyDataType } from "../../backend/constants/datatypes";
import IMFilesPropertyDefinition from "../../backend/interfaces/mfiles/IMFilesPropertyDefinition";
import {
  parseSelectedItemsFromQueryString,
  isSelectedItemsInQueryString,
  isSelectedViewInQueryString,
  parseViewIdFromQueryString,
  isSearchParamsInQueryString,
  parseSearchParamsFromQueryString,
} from "../../backend/helpers/urlHelper";
import { calculateZoomModeFromDateRange, getLargestDateRangeFromItems, initialEndDate, initialStartDate } from "../../backend/helpers/dateHelpers";
import { WARN, NOTIFY, PROGRESS } from "../../gantt/components/common/NotificationMessage";
import { IMFilesProperty, IMfilesObject } from "../../backend/interfaces/mfiles/IMFilesObject";
import {
  createMfilesProperty,
} from "../../backend/helpers/propertyHelpers";
import { addObjectAsync } from "../../backend/services/AddItemsService";
import { allObjectTypesSelector, objectTypeConfigurationSelector } from "../selectors/selectors";
import i18n from "../../i18n";
import { ICheckedoutObject } from "src/backend/interfaces/responses/IItemsResponse";
import { isOwnProperty } from "src/backend/helpers/configurationHelpers";
import { translateValueListItemName } from "src/backend/helpers/translationHelpers";
import { IObjectInsert } from "../interfaces/IObjectInsert";
import { iRootState, Dispatch } from "../store";
import sortBy from "lodash/sortBy";
import toNumber from "lodash/toNumber";

export default createModel()({
  state: {
    allData: [],
    dateRange: [initialStartDate(), initialEndDate()],
    moreResults: false,
    selectedItem: null,
    editedItem: null,
    loading: false,
    updating: false,
    lastUpdate: null
  } as IItemsState,
  reducers: {
    resetItems: (state: IItemsState): IItemsState => {
      return { ...state, allData: [], selectedItem: null, editedItem: null };
    },
    addItem: (state: IItemsState, payload: IItem): IItemsState => {
      return { ...state, allData: [...state.allData, { ...payload, id: state.allData.length }] };
    },
    addManyItems: (state: IItemsState, payload: Array<IItem>): IItemsState => {
      return {
        ...state,
        allData: [...state.allData, ...payload.map((item, index) => ({ ...item, id: state.allData.length + index }))],
      };
    },
    setMoreResults: (state: IItemsState, payload: boolean): IItemsState => {
      return {
        ...state,
        moreResults: payload,
      };
    },
    setLoading: (state: IItemsState, payload: boolean): IItemsState => {
      return {
        ...state,
        loading: payload,
      };
    },
    setUpdating: (state: IItemsState, payload: boolean): IItemsState => {
      return {
        ...state,
        updating: payload,
      };
    },
    setLastUpdate: (state: IItemsState, payload: Date): IItemsState => {
      return {
        ...state,
        lastUpdate: payload,
      };
    },

    _updateDateRange: (state: IItemsState, payload: [Date, Date]): IItemsState => {
      return {
        ...state,
        dateRange: payload,
      };
    },

    _updateMfilesPropertiesOfMany: produce((state: IItemsState, payload: IUpdateMfilesPropertiesOfMany) => {
      for (const { id, mfPropertyUpdates } of payload) {
        const item = state.allData[id];
        for (const update of mfPropertyUpdates) {
          const prop = item.properties.find((p) => p.property === update.propertyId);
          if (prop) {
            prop.value = update.newValue;
          } else {
            // Property didn't exist -> insert it
            item.properties = [
              ...item.properties,
              {
                dataType: update.dataType,
                displayValue: "",
                property: update.propertyId,
                value: update.newValue,
              },
            ];
          }
        }
      }
    }),
    _updateOwnPropertiesOfMany: produce((state: IItemsState, payload: IUpdateOwnPropertiesOfMany) => {
      for (const { id, ownPropertyUpdates } of payload) {
        for (const update of ownPropertyUpdates) {
          state.allData[id][update.propertyName] = update.newValue;
        }
      }
    }),
    _updateItem: produce((state: IItemsState, id: number, item: IItem) => {
      item.id = id;
      state.allData[id] = item;
    }),
    _updateItems: produce((state: IItemsState, items: IItem[]) => {
      state.allData = items;
    }),

    selectItem: produce((state: IItemsState, taskId: number) => {
      return {
        ...state,
        selectedItem: taskId,
      };
    }),
    editItem: produce((state: IItemsState, taskId: number) => {
      return {
        ...state,
        editedItem: taskId,
      };
    }),
  },
  effects: (dispatch0) => ({
    fetchItemsAsync: async (initializeDateRange: boolean, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      dispatch.items.resetItems();
      dispatch.items.setLoading(true);
      dispatch.items.setLastUpdate(new Date());

      let items: Array<IItem> = [];
      let moreResults: boolean;

      if (rootState.common.configuration?.baseConfiguration?.devModeOn) {
        ({ items, moreResults } = getTestData(rootState.items.dateRange[0], rootState.common.configuration));
      } else {
        const selectedDateRange = initializeDateRange ? null :  rootState.items.dateRange;
        const relevantObjectTypes = allObjectTypesSelector(rootState);
        if (isSelectedItemsInQueryString(relevantObjectTypes)) {
          // Some items have been selected in Mfiles and information about them (objectType, objectId) transmitted in query string.
          // Fetch only those items, filtered by which objectTypes fit our viewConfigurations.
          const selectedItemsInfo = parseSelectedItemsFromQueryString(relevantObjectTypes);
          const response = await getSelectedItemsAsync(
            rootState.common.httpClient,
            rootState.common.configuration,
            rootState.common.configuration.fetchedItemsCountLimit,
            selectedItemsInfo,
            selectedDateRange,
          );
          const objectTypesWithoutDates = Array.from(objectTypeConfigurationSelector(rootState as never).values())
            .filter((otc) => otc.dateProperties == null)
            .map((otc) => otc.objectTypeId);
          items = response.items?.map((item) => {
            if (objectTypesWithoutDates.includes(item.objectType)) {
              return { ...item, start: null, end: null };
            } else {
              return { ...item };
            }
          });
          moreResults = response.moreResults;
          if(initializeDateRange) {
            dispatch.items._updateDateRange(response.dateRange);
            const zoomMode = calculateZoomModeFromDateRange(response.dateRange[1], response.dateRange[0]);
            dispatch.UI.setZoomMode(zoomMode);
          }
        } else if (isSelectedViewInQueryString()) {
          // A view has been selected in mfiles.
          const viewId = parseViewIdFromQueryString();
          try {
            const itemsResponse = await getViewItemsWithPropertiesAsync(rootState.common.httpClient, rootState.common.configuration, viewId, rootState.common.configuration.fetchedItemsCountLimit, selectedDateRange);
            const objectTypesWithoutDates = Array.from(objectTypeConfigurationSelector(rootState as never).values())
              .filter((otc) => otc.dateProperties == null)
              .map((otc) => otc.objectTypeId);
            const objectTypesWithDatesAndNotFetchAlways =  Array.from(objectTypeConfigurationSelector(rootState as never).values())
              .filter((otc) => otc.dateProperties != null && otc.fetchAlways === false)
              .map((otc) => otc.objectTypeId);
            const itemsWithDatesAndNotFetchAlways = itemsResponse.filter(i => objectTypesWithDatesAndNotFetchAlways.includes(i.objectType));
            const dateRange = getLargestDateRangeFromItems(itemsWithDatesAndNotFetchAlways, rootState.common.configuration.maxDateRangeInYears);
            const itemsWithProperDates = itemsResponse.map((item) => {
              if (objectTypesWithoutDates.includes(item.objectType)) {
                return { ...item, start: null, end: null };
              } else {
                return { ...item };
              }
            });
            items = itemsWithProperDates;
            moreResults = false;
            if(initializeDateRange) {
              dispatch.items._updateDateRange(dateRange);
              const zoomMode = calculateZoomModeFromDateRange(dateRange[1], dateRange[0]);
              dispatch.UI.setZoomMode(zoomMode);
            }
          } catch (e) {
            console.error(e);
            dispatch.UI.showMessage(i18n.t("error.unknown", {err: e.message}), WARN);
          }
        } else if (isSearchParamsInQueryString()) {
          // An exported search has beem selected in mfiles.
          const searchParam = parseSearchParamsFromQueryString();
          try {
            const itemsResponse = await getSearchItemsWithPropertiesAsync(rootState.common.httpClient, rootState.common.configuration, searchParam, rootState.common.configuration.fetchedItemsCountLimit, selectedDateRange);
            const objectTypesWithoutDates = Array.from(objectTypeConfigurationSelector(rootState as never).values())
              .filter((otc) => otc.dateProperties == null)
              .map((otc) => otc.objectTypeId);
            const objectTypesWithDatesAndNotFetchAlways =  Array.from(objectTypeConfigurationSelector(rootState as never).values())
              .filter((otc) => otc.dateProperties != null && otc.fetchAlways === false)
              .map((otc) => otc.objectTypeId);
            const itemsWithDatesAndNotFetchAlways = itemsResponse.filter(i => objectTypesWithDatesAndNotFetchAlways.includes(i.objectType));
            const dateRange = getLargestDateRangeFromItems(itemsWithDatesAndNotFetchAlways, rootState.common.configuration.maxDateRangeInYears);
            const itemsWithProperDates = itemsResponse.map((item) => {
              if (objectTypesWithoutDates.includes(item.objectType)) {
                return { ...item, start: null, end: null };
              } else {
                return { ...item };
              }
            });
            items = itemsWithProperDates;
            moreResults = false;
            if(initializeDateRange) {
              dispatch.items._updateDateRange(dateRange);
              const zoomMode = calculateZoomModeFromDateRange(dateRange[1], dateRange[0]);
              dispatch.UI.setZoomMode(zoomMode);
            }
          } catch (e) {
            console.error(e);
            dispatch.UI.showMessage(i18n.t("error.unknown", {err: e.message}), WARN);
          }
        } else {
          // No items have been selected in Mfiles. Fetch items based on a daterange.
          ({ items, moreResults } = await getItemsWithPropertiesAsync(
            rootState.common.httpClient,
            rootState.common.configuration,
            rootState.items.dateRange,
            null,
            rootState.common.configuration.fetchedItemsCountLimit
          ));
          if(initializeDateRange) {
            const zoomMode = calculateZoomModeFromDateRange(rootState.items.dateRange[1], rootState.items.dateRange[0]);
            dispatch.UI.setZoomMode(zoomMode);
          }
        }
      }
      items = sortBy(items, ["start", "name"]);
      for(let i = 0; i < items.length; i++) {
        const item = items[i];
        const textFields = getTextFields(rootState, item);
        items[i] = {...item, ...textFields, id: i};
      }
      if(moreResults) {
        dispatch.UI.showMessage(i18n.t("inform.maxitemsreached", {count: rootState.common.configuration.fetchedItemsCountLimit}));
      }
      dispatch.items.setMoreResults(moreResults);
      dispatch.items.addManyItems(items);
      dispatch.items.setLoading(false);
    },
    refreshItemsAsync: async (_: null, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      if (rootState.common.isAuthenticated && 
        !rootState.common.configuration?.baseConfiguration?.devModeOn && 
        !rootState.items.loading && 
        !rootState.items.updating) {

        const lastUpdate = rootState.items.lastUpdate;
        const currentUpdate = new Date();
        const timeSinceLastUpdate = differenceInMinutes(currentUpdate, lastUpdate);

        if(!rootState.UI.displaySettings.refreshInterval || timeSinceLastUpdate < rootState.UI.displaySettings.refreshInterval) {
          //Refreshing not enabled or refresh interval not reached
          return;
        }

        let lastUpdateUtc = new Date(
          lastUpdate.getUTCFullYear(), lastUpdate.getUTCMonth(), lastUpdate.getUTCDate(),
          lastUpdate.getUTCHours(), lastUpdate.getUTCMinutes(), lastUpdate.getUTCSeconds());

        lastUpdateUtc = addMinutes(lastUpdateUtc, -1); //for clock differences

        dispatch.items.setUpdating(true);

        try {

          const config: IConfiguration = rootState.common.configuration;
          const allItems : IItem[] = rootState.items.allData;
        
          const changedItems = await fetchChangedItemsAsync(
            rootState.common.httpClient,
            rootState.common.configuration,
            lastUpdateUtc,
            rootState.common.configuration.fetchedItemsCountLimit );

          for(const changedItem of changedItems) {
            const existingItem = allItems.find((item) => item.objectType === changedItem.ObjVer.Type && item.objectId === changedItem.ObjVer.ID);
            if(existingItem && existingItem.objectVersion < changedItem.ObjVer.Version) {
              for(const objectTypeConfiguration of config.objectTypeConfigurations.values()) {
                if(objectTypeConfiguration.objectTypeId === existingItem.objectType) {
                  let changedItemWithProperties = await fetchSpecificItemPropertiesAsync(rootState.common.httpClient, objectTypeConfiguration, changedItem);
                  const textFields = getTextFields(rootState, changedItemWithProperties);
                  changedItemWithProperties = {...changedItemWithProperties, ...textFields};
                  dispatch.items._updateItem(existingItem.id, changedItemWithProperties);
                }
              }
            }
          }

          //Refresh items
          const relevantObjectTypes = allObjectTypesSelector(rootState);
          if (!isSelectedItemsInQueryString(relevantObjectTypes)) {
            let refreshedItems: IMfilesObject[] = null;
            if (isSelectedViewInQueryString()) { 
              const viewId = parseViewIdFromQueryString();
              const viewResult = await getViewItemsAsync(
                rootState.common.httpClient,
                rootState.common.configuration,
                viewId,
                rootState.common.configuration.fetchedItemsCountLimit);
              refreshedItems = viewResult.Items;
            } else if (isSearchParamsInQueryString()) {
              const searchParam = parseSearchParamsFromQueryString();
              const searchResult = await getSearchItemsAsync(
                rootState.common.httpClient,
                rootState.common.configuration,
                searchParam,
                rootState.common.configuration.fetchedItemsCountLimit);
              refreshedItems = searchResult.Items;
            } else {
              const dateRangeResult = await getItemsAsync(
                rootState.common.httpClient,
                rootState.common.configuration,
                rootState.items.dateRange,
                lastUpdateUtc,
                rootState.common.configuration.fetchedItemsCountLimit);
              refreshedItems = dateRangeResult.Items;
            }
            if(refreshedItems?.length > 0) {
              const mfilesObjects: IMfilesObject[] = [];
              for(const refreshedItem of refreshedItems) {
                const existingItem = allItems.find((item) => item.objectType === refreshedItem.ObjVer.Type && item.objectId === refreshedItem.ObjVer.ID);
                if(!existingItem) {
                  mfilesObjects.push(refreshedItem);
                }
              }
              if(mfilesObjects.length > 0) {
                const objVersOfItems = mfilesObjects.map((mfo) => mfo.ObjVer);
                const propertiesOfMultipleObjects = await getPropertiesOfMultipleObjectsAsync(rootState.common.httpClient, objVersOfItems);
                let items = mapMFilesItemWithPropertiesToItem(rootState.common.configuration.objectTypeConfigurations, mfilesObjects, propertiesOfMultipleObjects);
                items = sortBy(items, ["start", "name"]);
                for(let i = 0; i < items.length; i++) {
                  const item = items[i];
                  const textFields = getTextFields(rootState, item);
                  items[i] = {...item, ...textFields, id: i};
                }
                dispatch.items.addManyItems(items);
              }
            }
          }

          let checkedoutItems: ICheckedoutObject[] = [];
          try {
            checkedoutItems = await fetchCheckedoutItems(
              rootState.common.httpClient,
              rootState.common.configuration );
          } catch(e) {
            console.log("Getting checkedout items failed");
          }

          for(const objectTypeConfiguration of config.objectTypeConfigurations.values()) {
            const allIds = allItems.filter((item) => item.objectType === objectTypeConfiguration.objectTypeId).map((item) => item.objectId);
            const oldCheckedOutIds = allItems.filter((item) => item.objectType === objectTypeConfiguration.objectTypeId && item.isCheckedOut).map((item) => item.objectId);
            const newCheckedOutIds = checkedoutItems.filter((item) => item.ObjectType === objectTypeConfiguration.objectTypeId && allIds.includes(item.ID)).map((item) => item.ID);
            const removedCheckoutIds = oldCheckedOutIds.filter((item) => !newCheckedOutIds.includes(item));
            const addedCheckoutIds = newCheckedOutIds.filter((item) => !oldCheckedOutIds.includes(item));
            for(const removedCheckoutId of removedCheckoutIds) {
              const existingItem = allItems.find((item) => item.objectType === objectTypeConfiguration.objectTypeId && item.objectId === removedCheckoutId);
              if(existingItem) {
                const updatedItem: IItem = {
                  ...existingItem, 
                  isCheckedOut: false, 
                  isCheckedOutToThisUser: false
                };
                dispatch.items._updateItem(updatedItem.id, updatedItem);
              } 
            }
            for(const addedCheckoutId of addedCheckoutIds) {
              const existingItem = allItems.find((item) => item.objectType === objectTypeConfiguration.objectTypeId && item.objectId === addedCheckoutId);
              const checkedoutItem = checkedoutItems.find((item) => item.ID === addedCheckoutId);
              if(existingItem && checkedoutItem) {
                const updatedItem: IItem = {
                  ...existingItem, 
                  isCheckedOut: checkedoutItem.IsCheckedOut, 
                  isCheckedOutToThisUser: checkedoutItem.IsCheckedOutToThisUser
                };
                dispatch.items._updateItem(updatedItem.id, updatedItem);
              } 
            }
          }

          dispatch.items.setLastUpdate(currentUpdate);

        } catch(e) {
          console.log(e);
          console.log("Automatic refresh failed");
        }

        dispatch.items.setUpdating(false);
      }
    },
    moveOrResizeManyAsync: async (payload: IMoveOrResizeManyPayload, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      const updates: Array<IObjectUpdate> = [];

      for (const itemId in payload) {
        const item: IItem = rootState.items.allData[itemId];
        const { newStart, newEnd } = payload[itemId];
        const objectTypeConfiguration: IObjectTypeConfigurationItem = rootState.common.configuration.objectTypeConfigurations.get(
          item.objectType
        );
        if (objectTypeConfiguration?.dateProperties == null || objectTypeConfiguration.dateProperties.length < 2) {
          return;
        }

        // Set M-Files properties (in properties-collection)
        const mfPropertyUpdates: IMfilesPropertyUpdate[] = [];
        const [startDatePropertyId, endDatePropertyId] = objectTypeConfiguration.dateProperties
          ? objectTypeConfiguration.dateProperties
          : [null, null];
        const [startTimePropertyId, endTimePropertyId] = objectTypeConfiguration.timeProperties
          ? objectTypeConfiguration.timeProperties
          : [null, null];
        mfPropertyUpdates.push(
          ...createDateUpdates(
            item,
            newStart,
            getPropertyDefinition(rootState, startDatePropertyId),
            getPropertyDefinition(rootState, startTimePropertyId)
          )
        );
        mfPropertyUpdates.push(
          ...createDateUpdates(
            item,
            newEnd,
            getPropertyDefinition(rootState, endDatePropertyId),
            getPropertyDefinition(rootState, endTimePropertyId)
          )
        );

        updates.push({
          id: parseInt(itemId),
          objectId: item.objectId,
          objectType: item.objectType,
          objectVersion: item.objectVersion,
          mfPropertyUpdates: mfPropertyUpdates,
          ownPropertyUpdates: [
            {
              propertyName: "start",
              newValue: newStart,
              oldValue: item.start,
            },
            {
              propertyName: "end",
              newValue: newEnd,
              oldValue: item.end,
            },
          ],
        });
      }

      if (await dispatch.items.updateManyAsync(updates)) {
        dispatch.history.add(updates);
      }
    },
    deleteItemAsync: async () => {
      console.log("Won't delete");
      // This won't be implemented. Users shouldn't be able to delete items from Gantt according to Mika Tikkanen and Janne Hakala.
      // Also deleting items is in practice handled by changing object state instead of actually deleting. Retains history etc.
      // These differ from customer to customer.
    },
    insertItemAsync: async (payload: IObjectInsert, rootState: iRootState) => {

      const dispatch = dispatch0 as Dispatch;
      const objectTypeConfig = rootState.common.configuration.objectTypeConfigurations.get(payload.objectType);
      const properties: IMFilesProperty[] = payload.properties.map(i => createMfilesProperty(i.propertyId, i.dataType, i.value));

      try {
        dispatch.UI.showMessage(i18n.t("items.creating"), PROGRESS);
        const newObject = await addObjectAsync(rootState.common.httpClient, payload.objectType, { PropertyValues: properties });
        const newItem = await fetchSpecificItemPropertiesAsync (rootState.common.httpClient, objectTypeConfig, newObject);
        const textFields = getTextFields(rootState, newItem);
        const newItemWithTextFieldsAndId = {...newItem, ...textFields, id: rootState.items.allData.length};
        await dispatch.items.addItem(newItemWithTextFieldsAndId);
        dispatch.UI.showMessage(i18n.t("items.created"), NOTIFY);
      } catch (err) {
        dispatch.UI.showMessage(i18n.t("error.failedtocreate", {err: err.response?.data?.Message ?? err}), WARN);
      }
    },
    updateManyAsync: async (updates: Array<IObjectUpdate>, rootState: iRootState): Promise<boolean> => {
      const dispatch = dispatch0 as Dispatch;
      // Update in gantt
      dispatch.items._updateOwnPropertiesOfMany(updates);
      dispatch.items._updateMfilesPropertiesOfMany(updates);

      if (!rootState.common.configuration.baseConfiguration.devModeOn) {
        try {
          // Update in m-files
          dispatch.UI.showMessage(i18n.t("items.updating"), PROGRESS);
          const updatedItems = await updateManyObjectsAsync(rootState.common.httpClient, rootState.common.configuration, updates);
          for(const updatedItem of updatedItems) {
            const textFields = getTextFields(rootState, updatedItem);
            const updatedItemWithTexts = {...updatedItem, ...textFields};
            dispatch.items._updateItem(updatedItemWithTexts.id, updatedItemWithTexts);
          }
          dispatch.UI.showMessage(i18n.t("items.updated"), NOTIFY);
        } catch (error) {
          // Failed to update in m-files. Pending changes have been rolled back and items checked in.
          dispatch.UI.showMessage(i18n.t("error.failedtoupdate", {err: error?.response?.data?.Message ?? error}), WARN);
          console.log(error?.response?.data?.Message ?? error);

          // Rewind changes
          const reverted = updates.map(revertUpdate);
          dispatch.items._updateOwnPropertiesOfMany(reverted);
          dispatch.items._updateMfilesPropertiesOfMany(reverted);
          return false;
        }
      }
      return true;
    },
    toggleLink: async ({ linkFromId, linkToId }: { linkFromId: number; linkToId: number }, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      const linkFrom: IItem = rootState.items.allData[linkFromId];
      const linkTo: IItem = rootState.items.allData[linkToId];
      const suitableLinkConfig: ILinkConfigurationItem | undefined = rootState.common.configuration.linkConfigurations.find(
        (singleLinkConfig: ILinkConfigurationItem) =>
          singleLinkConfig.from === linkFrom.objectType && singleLinkConfig.to === linkTo.objectType
      );

      if (suitableLinkConfig) {
        const propertyDefinition: IMFilesPropertyDefinition = getPropertyDefinition(rootState, suitableLinkConfig.property);
        if (!propertyDefinition) {
          throw Error(i18n.t("error.propertydefinitionnotfound", {id: suitableLinkConfig.property}));
        }
        if (propertyDefinition.DataType !== PropertyDataType.Lookup && propertyDefinition.DataType !== PropertyDataType.MultiSelectLookup) {
          throw Error(i18n.t("error.propertydefinitionnotlookup", {id: propertyDefinition.ID, name: propertyDefinition.Name}));
        }
        const propertyOfItem = getProperty(linkTo.properties, suitableLinkConfig.property);
        const newValue = toggleInLookupPropertyValue(propertyOfItem.dataType, propertyOfItem.value, linkFrom.objectId.toString());
        const update: IObjectUpdate = {
          id: linkToId,
          objectId: linkTo.objectId,
          objectType: linkTo.objectType,
          objectVersion: linkTo.objectVersion,
          ownPropertyUpdates: [],
          mfPropertyUpdates: [
            {
              propertyId: suitableLinkConfig.property,
              dataType: propertyDefinition.DataType,
              oldValue: propertyOfItem?.value,
              newValue: newValue,
            },
          ],
        };

        await dispatch.items.updateManyAsync([update]);
      }
    },

    updateDateRangeAsync: async (payload: [Date, Date]) => {
      const dispatch = dispatch0 as Dispatch;
      dispatch.items._updateDateRange(payload);

      const zoomMode = calculateZoomModeFromDateRange(payload[1], payload[0]);
      dispatch.UI.setZoomMode(zoomMode);

      // In case there's a selected view or items in url, reset query params
      // Middle parameter is an empty string as per https://developer.mozilla.org/en-US/docs/Web/API/History/replaceState
      //window.history.replaceState({}, "", removeQueryParameters(window.location.href));

      if (payload == null) {
        dispatch.items.resetItems();
      } else {
        await dispatch.items.fetchItemsAsync(false);
      }
    },

    removeViewItem: async (payload: IRemoveViewItemPayload, rootState: iRootState): Promise<boolean> => {
      const dispatch = dispatch0 as Dispatch;
      const resourceItem: IItem = rootState.items.allData[payload.resourceItemId];
      const taskItem: IItem = rootState.items.allData[payload.taskItemId];
      const viewConfiguration = rootState.common.configuration.viewConfigurations[payload.viewIndex];
      for(const hierarchy of viewConfiguration.objectHierarchy.values()) {
        if(hierarchy.id === resourceItem.objectType) {
          for(const [taskObjectType, taskResourceProperty] of hierarchy.childObjectTypes) {
            if(taskObjectType === taskItem.objectType) {
              const property = getProperty(taskItem.properties, taskResourceProperty);
              const dataType = getPropertyDefinition(rootState, taskResourceProperty)?.DataType || PropertyDataType.Uninitialized;
              if(isInLookupPropertyValue(dataType, property.value, resourceItem.objectId.toString())) {
                const propertyUpdate: IMfilesPropertyUpdate = {
                  propertyId: taskResourceProperty,
                  oldValue: property?.value,
                  newValue: removeFromLookupPropertyValue(property.dataType, property.value, resourceItem.objectId.toString()),
                  dataType: dataType,
                };
                const objectUpdate: IObjectUpdate = {
                  id: taskItem.id,
                  objectId: taskItem.objectId,
                  objectType: taskItem.objectType,
                  objectVersion: taskItem.objectVersion,
                  mfPropertyUpdates: [propertyUpdate],
                  ownPropertyUpdates: [],
                };
                await dispatch.items.updateManyAsync([objectUpdate]);
                return true;
              }
            }
          }
        }
      }
    },
    dragndropAsync: async (payload: IDragNDropPayload, rootState: iRootState): Promise<boolean> => {
      // An item is dragged and dropped on another item
      // As a result the objectId of the dragged items should be set as value of drop target item's some property
      // or vice versa. The behavior can be configured.
      //
      // Examples of what we want to do:
      // Eg. Workorder dragged to Employee
      //      -> Workorder's property ResponsiblePerson gets drop target employee's objectId (assigns the workorder to employee)
      // Eg. Employee dragged to Workorder
      //      -> Again workorder's property ResponsiblePerson gets drop target employee's objectId (assigns the workorder to employee)

      const dispatch = dispatch0 as Dispatch;
      const dragSourceItem: IItem = rootState.items.allData[payload.sourceItemId];
      const dropTargetItem: IItem = rootState.items.allData[payload.targetItemId];

      const dndConfig: IDragAndDropConfigurationItem = rootState.common.configuration.dragndropConfigurations.find(
        (c: IDragAndDropConfigurationItem) =>
          c.sourceObjectType === dragSourceItem.objectType && c.targetObjectType === dropTargetItem.objectType
      );
      if (dndConfig) {
        const updates: IObjectUpdate[] = [];

        // Update drag source item
        if (dndConfig.sourcePropertyToUpdate != null) {
          const property = getProperty(dragSourceItem.properties, dndConfig.sourcePropertyToUpdate);
          const propertyUpdate: IMfilesPropertyUpdate = {
            propertyId: dndConfig.sourcePropertyToUpdate,
            oldValue: property?.value,
            newValue: toggleInLookupPropertyValue(property.dataType, property.value, dropTargetItem.objectId.toString()),
            dataType: getPropertyDefinition(rootState, dndConfig.sourcePropertyToUpdate)?.DataType || PropertyDataType.Uninitialized,
          };
          const objectUpdate: IObjectUpdate = {
            id: dragSourceItem.id,
            objectId: dragSourceItem.objectId,
            objectType: dragSourceItem.objectType,
            objectVersion: dragSourceItem.objectVersion,
            mfPropertyUpdates: [propertyUpdate],
            ownPropertyUpdates: [],
          };
          updates.push(objectUpdate);
        }

        // Update drop target item
        if (dndConfig.targetPropertyToUpdate != null) {
          const property = getProperty(dropTargetItem.properties, dndConfig.targetPropertyToUpdate);
          const propertyUpdate: IMfilesPropertyUpdate = {
            propertyId: dndConfig.targetPropertyToUpdate,
            oldValue: property?.value,
            newValue: toggleInLookupPropertyValue(property.dataType, property.value, dragSourceItem.objectId.toString()),
            dataType: getPropertyDefinition(rootState, dndConfig.targetPropertyToUpdate)?.DataType || PropertyDataType.Uninitialized,
          };
          const objectUpdate: IObjectUpdate = {
            id: dropTargetItem.id,
            objectId: dropTargetItem.objectId,
            objectType: dropTargetItem.objectType,
            objectVersion: dropTargetItem.objectVersion,
            mfPropertyUpdates: [propertyUpdate],
            ownPropertyUpdates: [],
          };
          updates.push(objectUpdate);
        }

        await dispatch.items.updateManyAsync(updates);
        return true;
      }

      return false;
    },
    updateTextFields: (_: null, rootState: iRootState): void => {
      const dispatch = dispatch0 as Dispatch;
      const items: IItem[] = [];
      for(let i = 0; i < rootState.items.allData.length; i++) {
        const item = rootState.items.allData[i];
        const textFields = getTextFields(rootState, item);
        items[i] = {...item, ...textFields};
      }
      dispatch.items._updateItems(items);
    }
  }),
});

const createDateUpdates = (
  item: IItem,
  newValue: Date | null,
  datePropertyDefinition: IMFilesPropertyDefinition,
  timePropertyDefinition?: IMFilesPropertyDefinition
): IMfilesPropertyUpdate[] => {
  const updates: IMfilesPropertyUpdate[] = [];
  const formattedDate = newValue ? format(newValue, MFILES_DATE_VALUE_FORMAT) : null;

  if (datePropertyDefinition) {
    updates.push({
      dataType: datePropertyDefinition.DataType,
      newValue: formattedDate,
      oldValue: getProperty(item.properties, datePropertyDefinition.ID)?.value,
      propertyId: datePropertyDefinition.ID,
    });
  }
  if (timePropertyDefinition) {
    updates.push({
      dataType: timePropertyDefinition.DataType,
      newValue: formattedDate,
      oldValue: getProperty(item.properties, timePropertyDefinition.ID)?.value,
      propertyId: timePropertyDefinition.ID,
    });
  }
  return updates;
};

const getPropertyDefinition = (rootState: iRootState, propertyId: number) => rootState.common.configuration.propertyDefinitions.get(propertyId);

const getTextFields = (rootState: iRootState, item: IItem): IItemTexts => {
  const fields: IItemTexts = {};
  const configuration: IConfiguration = rootState.common.configuration;
  const objectTypeConfiguration = configuration.objectTypeConfigurations.get(item.objectType);
  let gridColumns = objectTypeConfiguration.gridColumns;
  const userGridColumns = rootState.UI.displaySettings.gridColumns[objectTypeConfiguration.objectTypeId];
  if(userGridColumns) {
    gridColumns = userGridColumns;
  }
  for(let i = 0; i < gridColumns?.length; i++) {
    const textField = `text${i}`;
    const gridField = gridColumns[i];
    if(isOwnProperty(gridField.toString())) {
      fields[textField] = item[gridField] ?? "";
    } else {
      const property = getProperty(item.properties, gridField as number);
      fields[textField] = getPropertyValue(rootState, property) ?? "";
    }
  }
  return fields;
};

const getPropertyValue = (rootState: iRootState, property: IItemProperty): IItemText => {
  const timeFormat = rootState.UI.displaySettings.timeFormat;
  if( !property || property.value === null || property.value === undefined || property.value === "" ) {
    return null;
  }
  switch(property.dataType) {
    case PropertyDataType.Boolean: {
      const boolText: string = property.value ? i18n.t("boolean.true") : i18n.t("boolean.false");
      return boolText;
    }
    case PropertyDataType.Date:
      //remove trailing Z
      return parseISO(property.value.slice(0, -1));
    case PropertyDataType.Time:
      //remove trailing Z
      return format(parseISO(property.value.slice(0, -1)), timeFormat);
    case PropertyDataType.Lookup:
    case PropertyDataType.MultiSelectLookup: {
      const propertyDefinition = rootState.common.configuration.propertyDefinitions.get(property.property);
      if(propertyDefinition?.BasedOnValueList && propertyDefinition?.ValueList) {
        const valueListDefinition = rootState.common.configuration.valueListDefinitions.get(propertyDefinition.ValueList);
        if(valueListDefinition) {
          const propertyValues = property.value.split(",");
          const displayValues = property.displayValue.split(";");
          const translatedValues = [];
          for(let i = 0; i < propertyValues.length; i++) {
            const valueId = (propertyValues[i] as unknown) as number;
            const translatedValue = translateValueListItemName(propertyDefinition.ValueList, valueId, displayValues[i]);
            translatedValues.push(translatedValue);
          }
          return translatedValues.join(", ");
        }
      }
      return property?.displayValue ?? null;
    }
    case PropertyDataType.Integer:
    case PropertyDataType.Integer64:
    case PropertyDataType.Floating:
      return toNumber(property?.displayValue ?? null);
    default:
      return property?.displayValue ?? null;
  }
};
